import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ContractLiquidationAcceptanceMinutesService } from "services/contract-liquidation-acceptance-minutes";
import { getPrintTemplate } from "./components/printTemplate";

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts?.day || !parts?.month || !parts?.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

export default function ContractLiquidationAcceptanceMinutesDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({ day: "", month: "", year: "" });
  const [contractDateParts, setContractDateParts] = useState({ day: "", month: "", year: "" });
  const [form, setForm] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    contractNumber: "",
    assignorName: "",
    assignorTitle: "",
    assignorRepresentativeOf: "",
    assigneeName: "",
    assigneeTitle: "",
    assigneeRepresentativeOf: "",
    completedWorkContent: "",
    executedContractValue: "",
    amountPaid: "",
    penaltyAmount: "",
    paidByParty: "",
    paidToParty: "",
    penaltyByParty: "",
    remainingByParty: "",
    remainingToParty: "",
    remainingAmount: "",
    conclusion: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ContractLiquidationAcceptanceMinutesService.getById(id);
        const data = response.data ? response.data : response;
        setForm({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          contractNumber: data.contractNumber || "",
          assignorName: data.assignorName || "",
          assignorTitle: data.assignorTitle || "",
          assignorRepresentativeOf: data.assignorRepresentativeOf || "",
          assigneeName: data.assigneeName || "",
          assigneeTitle: data.assigneeTitle || "",
          assigneeRepresentativeOf: data.assigneeRepresentativeOf || "",
          completedWorkContent: data.completedWorkContent || "",
          executedContractValue: String(data.executedContractValue ?? ""),
          amountPaid: String(data.amountPaid ?? ""),
          penaltyAmount: String(data.penaltyAmount ?? ""),
          paidByParty: data.paidByParty || "",
          paidToParty: data.paidToParty || "",
          penaltyByParty: data.penaltyByParty || "",
          remainingByParty: data.remainingByParty || "",
          remainingToParty: data.remainingToParty || "",
          remainingAmount: data.remainingAmount || data.remainingAmountInWords || "",
          conclusion: data.conclusion || "",
        });

        setDocumentDateParts(splitDate(data.documentDate || ""));
        setContractDateParts(splitDate(data.contractDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên bản");
      }
    })();
  }, [id]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setDateField = (target, field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    const sanitized = field === "year" ? raw.slice(0, max) : raw.slice(-max);
    if (target === "document") {
      setDocumentDateParts((prev) => ({ ...prev, [field]: sanitized }));
      return;
    }
    setContractDateParts((prev) => ({ ...prev, [field]: sanitized }));
  };

  const padDatePartOnBlur = (target, field) => {
    if (field === "year") return;
    const setter = target === "document" ? setDocumentDateParts : setContractDateParts;
    setter((prev) => {
      const current = String(prev[field] || "");
      if (!current) return prev;
      return { ...prev, [field]: current.padStart(2, "0") };
    });
  };

  const buildPayload = () => {
    const documentDate = composeDate(documentDateParts);
    const contractDate = composeDate(contractDateParts);

    if (!documentDate) throw new Error("Ngày biên bản chưa hợp lệ");
    if (!contractDate) throw new Error("Ngày hợp đồng chưa hợp lệ");

    const required = [
      ["unit", "Đơn vị"],
      ["department", "Bộ phận"],
      ["documentNumber", "Số biên bản"],
      ["contractNumber", "Số hợp đồng"],
      ["assignorName", "Họ tên bên giao khoán"],
      ["assignorTitle", "Chức vụ bên giao khoán"],
      ["assignorRepresentativeOf", "Đại diện bên giao khoán"],
      ["assigneeName", "Họ tên bên nhận khoán"],
      ["assigneeTitle", "Chức vụ bên nhận khoán"],
      ["assigneeRepresentativeOf", "Đại diện bên nhận khoán"],
      ["completedWorkContent", "Nội dung công việc đã thực hiện"],
      ["paidByParty", "Bên đã thanh toán"],
      ["paidToParty", "Bên được thanh toán"],
      ["penaltyByParty", "Bên vi phạm hợp đồng"],
      ["remainingByParty", "Bên còn phải thanh toán"],
      ["remainingToParty", "Bên được nhận thanh toán còn lại"],
      ["remainingAmount", "Số tiền còn phải thanh toán (viết bằng chữ)"],
      ["conclusion", "Kết luận"],
    ];

    for (const [key, label] of required) {
      if (!String(form[key] || "").trim()) throw new Error(`${label} không được để trống`);
    }

    return {
      ...form,
      documentDate,
      contractDate,
      executedContractValue: String(form.executedContractValue || "").trim(),
      amountPaid: String(form.amountPaid || "").trim(),
      penaltyAmount: String(form.penaltyAmount || "").trim(),
      remainingAmount: String(form.remainingAmount || "").trim(),
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();

      if (id) {
        await ContractLiquidationAcceptanceMinutesService.update(id, payload);
        toast.success("Cập nhật biên bản thành công");
      } else {
        await ContractLiquidationAcceptanceMinutesService.create(payload);
        toast.success("Tạo biên bản thành công");
      }

      navigation("/templates/contract-liquidation-acceptance-minutes");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Không thể lưu biên bản thanh lý");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      data: {
        ...form,
        documentDate: composeDate(documentDateParts),
        contractDate: composeDate(contractDateParts),
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const inputStyle = { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: 14, fontFamily: "inherit", background: "transparent" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: 16, color: "#000", backgroundColor: "#fff", padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div><b>Đơn vị:</b> <input disabled={containView} style={{ ...inputStyle, width: 220 }} value={form.unit} onChange={(e) => setField("unit", e.target.value)} /></div>
                    <div><b>Bộ phận:</b> <input disabled={containView} style={{ ...inputStyle, width: 220 }} value={form.department} onChange={(e) => setField("department", e.target.value)} /></div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div><b>Mẫu số: 06 - LĐTL</b></div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>(Kèm theo Thông tư số 99/2025/TT-BTC<br />ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "10px 0 4px", fontWeight: 700 }}>BIÊN BẢN THANH LÝ (NGHIỆM THU) HỢP ĐỒNG GIAO KHOÁN</h2>

                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input disabled={containView} style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }} value={documentDateParts.day} onChange={(e) => setDateField("document", "day", e.target.value)} onBlur={() => padDatePartOnBlur("document", "day")} />
                  tháng
                  <input disabled={containView} style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }} value={documentDateParts.month} onChange={(e) => setDateField("document", "month", e.target.value)} onBlur={() => padDatePartOnBlur("document", "month")} />
                  năm
                  <input disabled={containView} style={{ ...inputStyle, width: 50, textAlign: "center", margin: "0 4px" }} value={documentDateParts.year} onChange={(e) => setDateField("document", "year", e.target.value)} />
                </div>

                <div style={{ textAlign: "right", marginBottom: 10 }}>Số: <input disabled={containView} style={{ ...inputStyle, width: 150 }} value={form.documentNumber} onChange={(e) => setField("documentNumber", e.target.value)} /></div>

                <div style={{ marginBottom: 8 }}>
                  Họ và tên: <input disabled={containView} style={{ ...inputStyle, width: 180 }} value={form.assignorName} onChange={(e) => setField("assignorName", e.target.value)} />
                  &nbsp; Chức vụ <input disabled={containView} style={{ ...inputStyle, width: 140 }} value={form.assignorTitle} onChange={(e) => setField("assignorTitle", e.target.value)} />
                  &nbsp; Đại diện <input disabled={containView} style={{ ...inputStyle, width: 160 }} value={form.assignorRepresentativeOf} onChange={(e) => setField("assignorRepresentativeOf", e.target.value)} /> Bên giao khoán
                </div>
                <div style={{ marginBottom: 8 }}>
                  Họ và tên: <input disabled={containView} style={{ ...inputStyle, width: 180 }} value={form.assigneeName} onChange={(e) => setField("assigneeName", e.target.value)} />
                  &nbsp; Chức vụ <input disabled={containView} style={{ ...inputStyle, width: 140 }} value={form.assigneeTitle} onChange={(e) => setField("assigneeTitle", e.target.value)} />
                  &nbsp; Đại diện <input disabled={containView} style={{ ...inputStyle, width: 160 }} value={form.assigneeRepresentativeOf} onChange={(e) => setField("assigneeRepresentativeOf", e.target.value)} /> Bên nhận khoán
                </div>

                <div style={{ marginBottom: 8 }}>
                  Cùng thanh lý Hợp đồng số <input disabled={containView} style={{ ...inputStyle, width: 160 }} value={form.contractNumber} onChange={(e) => setField("contractNumber", e.target.value)} /> ngày
                  <input disabled={containView} style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }} value={contractDateParts.day} onChange={(e) => setDateField("contract", "day", e.target.value)} onBlur={() => padDatePartOnBlur("contract", "day")} /> tháng
                  <input disabled={containView} style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }} value={contractDateParts.month} onChange={(e) => setDateField("contract", "month", e.target.value)} onBlur={() => padDatePartOnBlur("contract", "month")} /> năm
                  <input disabled={containView} style={{ ...inputStyle, width: 50, textAlign: "center", margin: "0 4px" }} value={contractDateParts.year} onChange={(e) => setDateField("contract", "year", e.target.value)} />
                </div>

                <div style={{ marginBottom: 8 }}>
                  Nội dung công việc (ghi trong hợp đồng) đã được thực hiện:
                  <input disabled={containView} style={{ ...inputStyle, width: "56%", marginLeft: 6 }} value={form.completedWorkContent} onChange={(e) => setField("completedWorkContent", e.target.value)} />
                </div>

                <div style={{ marginBottom: 8 }}>
                  Giá trị hợp đồng đã thực hiện:
                  <input disabled={containView} style={{ ...inputStyle, width: 260, marginLeft: 6 }} value={form.executedContractValue} onChange={(e) => setField("executedContractValue", e.target.value)} /> đồng
                </div>

                <div style={{ marginBottom: 8 }}>
                  Bên <input disabled={containView} style={{ ...inputStyle, width: 120 }} value={form.paidByParty} onChange={(e) => setField("paidByParty", e.target.value)} /> đã thanh toán cho bên
                  <input disabled={containView} style={{ ...inputStyle, width: 120, marginLeft: 4 }} value={form.paidToParty} onChange={(e) => setField("paidToParty", e.target.value)} /> số tiền là
                  <input disabled={containView} style={{ ...inputStyle, width: 260, margin: "0 4px" }} value={form.amountPaid} onChange={(e) => setField("amountPaid", e.target.value)} /> đồng (viết bằng chữ)
                </div>

                <div style={{ marginBottom: 8 }}>
                  Số tiền bị phạt do bên <input disabled={containView} style={{ ...inputStyle, width: 120 }} value={form.penaltyByParty} onChange={(e) => setField("penaltyByParty", e.target.value)} /> vi phạm hợp đồng:
                  <input disabled={containView} style={{ ...inputStyle, width: 260, margin: "0 4px" }} value={form.penaltyAmount} onChange={(e) => setField("penaltyAmount", e.target.value)} /> đồng (viết bằng chữ)
                </div>

                <div style={{ marginBottom: 8 }}>
                  Số tiền bên <input disabled={containView} style={{ ...inputStyle, width: 120 }} value={form.remainingByParty} onChange={(e) => setField("remainingByParty", e.target.value)} /> còn phải thanh toán cho bên
                  <input disabled={containView} style={{ ...inputStyle, width: 120, marginLeft: 4 }} value={form.remainingToParty} onChange={(e) => setField("remainingToParty", e.target.value)} /> là
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 220, margin: "0 4px", textAlign: "left" }}
                    value={form.remainingAmount}
                    onChange={(e) => setField("remainingAmount", e.target.value)}
                  />{" "}
                  (viết bằng chữ)
                </div>

                <div style={{ marginBottom: 8 }}>
                  Kết luận:
                  <input disabled={containView} style={{ ...inputStyle, width: "82%", marginLeft: 6 }} value={form.conclusion} onChange={(e) => setField("conclusion", e.target.value)} />
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>Trở về</SoftButton>
                  <SoftBox display="flex" gap={2}>
                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>In biên bản</SoftButton>
                    ) : (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {loadingSubmit ? "Đang lưu..." : id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
